import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Phone, MapPin, Mail, MessageCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    user_class: '',
    section: '',
    location: '',
    subject: '',
    problem: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);

  const contactInfo = {
    phones: ['+91 97167 94909', '+91 96255 83120', '+91 7827272198'],
    email: 'Studypoint9716@gmail.com',
    mapUrl: 'https://maps.app.goo.gl/iFAR95WE5K4G7V4v6?g_st=aw',
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getLocation = () => {
    setGettingLocation(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            const data = await response.json();
            setFormData({ ...formData, location: data.display_name || `${latitude}, ${longitude}` });
          } catch (error) {
            setFormData({ ...formData, location: `${latitude}, ${longitude}` });
          }
          setGettingLocation(false);
        },
        (error) => {
          toast.error('Could not get location. Please enter manually.');
          setGettingLocation(false);
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser.');
      setGettingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/feedback`, formData);
      toast.success('Feedback submitted successfully!');
      
      // Open mailto link
      window.location.href = response.data.mailto;
      
      setFormData({
        name: '',
        user_class: '',
        section: '',
        location: '',
        subject: '',
        problem: '',
      });
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Get in Touch</span>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
            Contact <span className="text-red-500">Us</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            Have questions or feedback? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <div className="space-y-6">
              {/* Phone Numbers */}
              <div className="card-hover bg-[#121212] p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="font-semibold text-white">Phone Numbers</h3>
                </div>
                <div className="space-y-2">
                  {contactInfo.phones.map((phone, idx) => (
                    <a
                      key={idx}
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      className="block text-zinc-400 hover:text-white transition-colors"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>

              {/* WhatsApp */}
              <div className="card-hover bg-[#121212] p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-green-500/10 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <h3 className="font-semibold text-white">WhatsApp</h3>
                </div>
                <div className="space-y-2">
                  {contactInfo.phones.map((phone, idx) => (
                    <a
                      key={idx}
                      href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-green-500 hover:text-green-400 transition-colors"
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="card-hover bg-[#121212] p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-blue-500" />
                  </div>
                  <h3 className="font-semibold text-white">Location</h3>
                </div>
                <a
                  href={contactInfo.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-400 transition-colors"
                >
                  View on Google Maps →
                </a>
              </div>

              {/* Email */}
              <div className="card-hover bg-[#121212] p-6 rounded-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-sm bg-yellow-500/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-yellow-500" />
                  </div>
                  <h3 className="font-semibold text-white">Email</h3>
                </div>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-yellow-500 hover:text-yellow-400 transition-colors"
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>

            {/* Map Embed */}
            <div className="mt-6 rounded-sm overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.456!2d77.0!3d28.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzAwLjAiTiA3N8KwMDAnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale"
              ></iframe>
            </div>
          </div>

          {/* Feedback Form */}
          <div className="card-hover bg-[#121212] p-8 rounded-sm">
            <h2 className="font-outfit text-2xl font-bold text-white mb-6">Send Feedback</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white"
                  placeholder="Your name"
                  data-testid="feedback-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Class *</label>
                  <Input
                    name="user_class"
                    value={formData.user_class}
                    onChange={handleChange}
                    required
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white"
                    placeholder="e.g., 10th"
                    data-testid="feedback-class"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Section *</label>
                  <Input
                    name="section"
                    value={formData.section}
                    onChange={handleChange}
                    required
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white"
                    placeholder="e.g., A"
                    data-testid="feedback-section"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Location</label>
                <div className="flex gap-2">
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white flex-1"
                    placeholder="Your location"
                    data-testid="feedback-location"
                  />
                  <Button
                    type="button"
                    onClick={getLocation}
                    disabled={gettingLocation}
                    variant="outline"
                    className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"
                  >
                    {gettingLocation ? 'Getting...' : 'Auto-detect'}
                  </Button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Subject *</label>
                <Input
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white"
                  placeholder="Subject of your feedback"
                  data-testid="feedback-subject"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Problem / Message *</label>
                <Textarea
                  name="problem"
                  value={formData.problem}
                  onChange={handleChange}
                  required
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white min-h-[120px]"
                  placeholder="Describe your problem or message..."
                  data-testid="feedback-problem"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded-sm py-6"
                data-testid="submit-feedback-btn"
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
